import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Briefcase, DollarSign, Users, Calendar, ChevronRight, Mic2 } from 'lucide-react';
import { UpcomingWeekWidget } from '../components/dashboard/UpcomingWeekWidget';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Dashboard = () => {
  const { getAuthHeaders, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [projectsInProgress, setProjectsInProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`, { headers: getAuthHeaders() });
      setStats(response.data.stats);
      setProjectsInProgress(response.data.projects_in_progress || []);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const parts = dateStr.split('T')[0].split('-');
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    } catch { return dateStr; }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ENTREGUE': return 'bg-green-500/20 text-green-400';
      case 'ATRASADO': return 'bg-red-500/20 text-red-400';
      default: return 'bg-orange-500/20 text-orange-400';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-teal-400"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-10 space-y-8" data-testid="dashboard">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white">
            Bem-vindo, {user?.artist_name || user?.name}!
          </h1>
          <p className="text-slate-400 mt-2">
            Seu cargo: <span className="text-teal-400 capitalize">{user?.role}</span>
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            icon={<Briefcase className="h-5 w-5" />}
            title="Projetos Ativos"
            value={stats?.active_projects || 0}
            color="teal"
          />
          <StatCard 
            icon={<DollarSign className="h-5 w-5" />}
            title="A Receber (Mês)"
            value={`R$ ${(stats?.pending_payment || 0).toFixed(2)}`}
            color="green"
          />
          <StatCard 
            icon={<Users className="h-5 w-5" />}
            title="Personagens Pendentes"
            value={stats?.pending_characters || 0}
            color="orange"
          />
          <StatCard 
            icon={<Mic2 className="h-5 w-5" />}
            title="Total de Falas a Gravar"
            value={stats?.total_falas || 0}
            color="purple"
          />
        </div>

        {/* Projects + Upcoming Week side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Projetos em Andamento */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 lg:col-span-2">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-teal-400" />
            Projetos em Andamento
          </h2>
          
          {projectsInProgress.length > 0 ? (
            <div className="space-y-4">
              {projectsInProgress.map((project) => (
                <Link 
                  key={project.project_id}
                  to={`/projects/${project.project_id}`}
                  className="block bg-slate-700/50 rounded-lg p-4 hover:bg-slate-700/70 transition-colors group"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-white font-medium group-hover:text-teal-400 transition-colors flex items-center gap-2">
                        {project.project_title}
                        <ChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </h3>
                      {project.client && (
                        <p className="text-slate-400 text-sm">Cliente: {project.client}</p>
                      )}
                    </div>
                    {project.delivery_deadline && (
                      <div className="flex items-center gap-1 text-sm text-slate-400">
                        <Calendar className="h-4 w-4" />
                        Entrega: {formatDate(project.delivery_deadline)}
                      </div>
                    )}
                  </div>
                  
                  {/* Characters */}
                  {project.characters && project.characters.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {project.characters.map((char) => (
                        <span 
                          key={char.id}
                          className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(char.status)}`}
                        >
                          {char.name}: {char.status}{char.num_falas ? ` (${char.num_falas} falas)` : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-slate-400">Nenhum projeto em andamento.</p>
          )}
          </div>

          {/* Próximos 7 dias */}
          <UpcomingWeekWidget getAuthHeaders={getAuthHeaders} />
        </div>
      </div>
    </Layout>
  );
};

const StatCard = ({ icon, title, value, color }) => {
  const colorClasses = {
    teal: 'bg-teal-500/10 text-teal-400 border-teal-500/30',
    green: 'bg-green-500/10 text-green-400 border-green-500/30',
    orange: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
  };

  return (
    <div className={`rounded-xl p-6 border ${colorClasses[color]}`} data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-400 text-sm">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
        </div>
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${colorClasses[color].split(' ').slice(0, 2).join(' ')}`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
